import { ElementRef, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BaseHttpService, PageResponse } from '../../auth/base-capacitor-http';
import { Yard } from './yard.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MovesReportComponent } from 'src/app/dialogs/moves-report/moves-report.component';
import { lastValueFrom } from 'rxjs';
import * as html2pdf from 'html2pdf.js';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EncryptedData } from 'src/app/auth/auth.service';
import { Capacitor } from '@capacitor/core';


export enum MoveStatus{
  PENDING = 'PENDING',
  INPROGRESS = 'INPROGRESS',
  COMPLETE = 'COMPLETE'
}

export enum MoveUpdateOption{
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  CANCEL = 'CANCEL',
  REDIRECT = 'REDIRECT',
  COMPLETE = 'COMPLETE'

}

export class Move{
  id: string;
  created: Date;
  completed: Date;
  start: Yard;
  end: Yard;
  trailerId: string;
  status: string;
  endYardId: string;
  startYardId: string;
}

export class MoveSearchParams{
  page: number;
  size: number;
  completed?: boolean;
  start?: number; // milliscocond from the date.getTime() method
  end?: number;
  sort?: MoveSortOption;
  search?: string;
}

export enum MoveSortOption{
  COMPLETE_NEW = 'COMPLETE-NEW',
  COMPLETE_OLD = 'COMPLETE-OLD',
  CREATED_OLD = 'CREATED-OLD',
  CREATED_NEW = 'CREATED-NEW'
}

@Injectable({
  providedIn: 'root',
})
export class MovesService extends BaseHttpService {
  private baseUrl: string =  environment.server.mainURL;
  private urlEndPoint: string;

  constructor(
    client: HttpClient, private dialog: MatDialog, snackbar: MatSnackBar
  ) {
    super(client, snackbar);
  }

  openMoveReportDialog(moves: Move[], email: string){
    const dialogRef: MatDialogRef<MovesReportComponent, boolean> = 
    this.dialog.open(MovesReportComponent, {
      width: '700px',
      height: '500px',
      maxWidth: '100vw',
    });
    dialogRef.componentInstance.moves = moves;
    dialogRef.componentInstance.email = email;

    return lastValueFrom(dialogRef.afterClosed());
  }
 
  async emailMovesReport(moves: Move[]): Promise<Boolean> {
    this.urlEndPoint = this.baseUrl + 'gen-moves-report';
    // senfing the data as a json works best for deserialization on the server side.
    let params = { moves: JSON.stringify(moves) };
    return this.post<Boolean>(this.urlEndPoint, params);
  }

  /**
   * 
   * @param search 
   * @param page 
   * @param size 
   * @returns 
   */
  async searchMovesByStatus(params: MoveSearchParams): Promise<PageResponse<Move>> {
    this.urlEndPoint = this.baseUrl + 'move';
    return this.get<PageResponse<Move>>(this.urlEndPoint, params);
  }

  /**
   * Return all completed moves within the give time ranges
   * ISO string from date object using  
   
   * @param start string = startDate.toISOString().slice(0, 19);
   * @param end string = startDate.toISOString().slice(0, 19);
   * @returns Promise<Move[]>
   */
  async getMovesReportData(start: number, end: number): Promise<Move[]>{
    this.urlEndPoint = this.baseUrl + 'moves-report-data';
    const params = {
      start: start,
      end: end
    }
    const encryptedResponse = await this.get<EncryptedData>(this.urlEndPoint, params);
    return this.decryptResponse<Move[]>(encryptedResponse.payload, this.KEY, encryptedResponse.iv);
  }

  /**
   * This does all of the move updates/create and deletes.
   * @param update CREATE, UPDATE, CANCEL, REDIRECT, COMPLETE
   * @param move 
   * @returns 
   */
  async updateMove(update: MoveUpdateOption, move: Move, redirect: Yard): Promise<Move> {
    this.urlEndPoint = this.baseUrl + 'move';
    let body = {
      move: JSON.stringify(move),
      redirect: JSON.stringify(redirect)
    }
    return this.put<Move>(this.urlEndPoint, body, { update: update });  
  }

  downLoadReport(element: HTMLElement){
  
     
      const options = {
        margin: 10,
        filename: 'receipt.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true, // Important if you have images or fonts from external sources
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        }
      };
  
      // Ensure the element exists
      if (!element) {
        console.error('ElementRef is invalid or not found');
        return;
      }
  
      html2pdf()
        .set(options)
        .from(element)
        .save()
        .catch(err => {
          console.error('PDF generation error:', err);
        });
  }

  async getHtmlBlob(element: HTMLElement){
    const options = {
      margin: 10,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    
    const worker = html2pdf()
      .set(options)
      .from(element);
    
    const blob: Blob = await worker.outputPdf('blob');
    return blob;
      /*
      const formData = new FormData();
      formData.append('file', blob, 'receipt.pdf');
  
      this.urlEndPoint = this.baseUrl + 'finance/email-receipt'
      const headers = await this.getAuthHeaders();
      if(Capacitor.isNativePlatform()){
        const res = await CapacitorHttp.post({ url: this.urlEndPoint, data: formData, headers: headers });
        return res.data as boolean;
      }
  
      return lastValueFrom(this.client.post<boolean>(this.urlEndPoint, formData, { headers: new HttpHeaders(headers) }));
      */
  }

  async emailReportBlob(blobs: Blob[], email: string){
    this.urlEndPoint = this.baseUrl + 'moves-report';
    const headers = await this.getMediaAuthHeaders();
   

    const formData = new FormData();
    formData.append('email', email);
    formData.append('reportType', 'Moves');
    blobs.forEach((blob, index) =>{
      formData.append('file', blob, 'moves-report (' + index + ' ).pdf');
    });

    return this.post<boolean>(this.urlEndPoint, formData, null, headers);
  }



}
